import {
  formatStoredAnswerValue,
  normalizeFormPersonListEntries,
  normalizePersonMatchToken,
} from './forms.ts';
import type {
  FormPersonListEntry,
  FormRoomAssignmentRow,
  FormRoomRow,
  FormSubmissionAnswerRow,
  FormSubmissionWithAnswers,
} from './forms.ts';

export type RoomSizePreference = {
  min: number;
  max: number | null;
  label: string;
};

export type RoomPlanningParticipant = {
  submission: FormSubmissionWithAnswers;
  submissionId: string;
  displayName: string;
  userId: string | null;
  normalizedName: string;
  aliases: Set<string>;
  requestedRoommateEntries: FormPersonListEntry[];
  requestedRoommates: string[];
  requestedRoommateTokens: Set<string>;
  preferredRoomSize: RoomSizePreference | null;
  wantsSingleRoom: boolean;
  notes: string;
};

export type ProposedRoomAssignment = {
  participant: RoomPlanningParticipant;
  roomId: string | null;
  notes: string;
};

export type BedroomRoomGroupInput = {
  id: string;
  count: number;
  capacity: number;
  labelPrefix?: string | null;
  sortOrder?: number | null;
};

export type BedroomRoomInsert = {
  contingent_id: string;
  name: string;
  capacity: number;
  floor: string;
  type: string;
  is_sleeping_room: boolean;
  notes: string;
  sort_order: number;
};

export type RoomPlanningConflict = {
  type:
    | 'ambiguous_wish'
    | 'duplicate_assignment'
    | 'duplicate_name'
    | 'group_too_large'
    | 'invalid_room'
    | 'missing_capacity'
    | 'missing_person'
    | 'non_sleeping_room_used'
    | 'one_way_conflict'
    | 'over_capacity'
    | 'partial_group'
    | 'profile_without_submission'
    | 'single_room_violation'
    | 'stale_assignment'
    | 'unmatched_wish'
    | 'wish_unfulfilled';
  label: string;
  detail: string;
  names: string[];
};

export type RoomWishGroupSummary = {
  participantIds: string[];
  names: string[];
  matchCount: number;
  isSingleGroup: boolean;
  mutualWishes: string[];
  oneWayWishes: string[];
  unresolvedWishes: string[];
};

type WishResolution =
  | { status: 'matched'; target: RoomPlanningParticipant; rawName: string; token: string; userId: string | null }
  | { status: 'ambiguous'; rawName: string; token: string; candidates: RoomPlanningParticipant[]; userId: string | null }
  | { status: 'unmatched'; rawName: string; token: string; userId: string | null };

type RelationshipGraph = {
  byParticipant: Map<string, WishResolution[]>;
  directedTargets: Map<string, Set<string>>;
  mutualPairs: Set<string>;
  weight: (a: RoomPlanningParticipant, b: RoomPlanningParticipant) => number;
};

export type RoomPlanningParticipantIdentityIndex = {
  bySubmissionId: Map<string, RoomPlanningParticipant>;
  byUserId: Map<string, RoomPlanningParticipant>;
  byUniqueName: Map<string, RoomPlanningParticipant>;
  byName: Map<string, RoomPlanningParticipant[]>;
};

export function normalizePlanningName(value: string): string {
  return normalizePersonMatchToken(value);
}

export function bedroomRoomPrefix(capacity: number, labelPrefix?: string | null): string {
  const prefix = (labelPrefix ?? '').trim();
  return prefix || `${capacity}er-Zimmer`;
}

export function buildMissingBedroomRoomInserts(
  groups: BedroomRoomGroupInput[],
  existingRooms: Array<Pick<FormRoomRow, 'contingent_id'>>,
): BedroomRoomInsert[] {
  const inserts: BedroomRoomInsert[] = [];

  for (const group of groups) {
    const count = Math.max(0, Math.round(group.count));
    const capacity = Math.max(0, Math.round(group.capacity));
    if (!group.id || count <= 0 || capacity <= 0) continue;

    const existingCount = existingRooms.filter((room) => room.contingent_id === group.id).length;
    const prefix = bedroomRoomPrefix(capacity, group.labelPrefix);
    const sortBase = Number.isFinite(group.sortOrder ?? Number.NaN) ? Number(group.sortOrder) : 0;

    for (let index = existingCount + 1; index <= count; index += 1) {
      inserts.push({
        contingent_id: group.id,
        name: `${prefix} ${index}`,
        capacity,
        floor: '',
        type: 'Schlafraum',
        is_sleeping_room: true,
        notes: '',
        sort_order: sortBase * 1000 + index,
      });
    }
  }

  return inserts;
}

export function normalizeBedroomRooms<T extends Pick<FormRoomRow, 'id' | 'name' | 'capacity'> & Partial<FormRoomRow>>(rooms: T[]): FormRoomRow[] {
  return rooms.map((room, index) => ({
    id: room.id,
    form_id: room.form_id ?? '',
    contingent_id: room.contingent_id ?? null,
    name: room.name,
    capacity: Number(room.capacity),
    floor: room.floor ?? '',
    type: 'Schlafraum',
    is_sleeping_room: true,
    notes: room.notes ?? '',
    sort_order: room.sort_order ?? index,
    is_locked: room.is_locked ?? false,
    created_at: room.created_at ?? '',
    updated_at: room.updated_at ?? '',
  }));
}

function answerText(answer: FormSubmissionAnswerRow | undefined): string {
  return answer ? formatStoredAnswerValue(answer).trim() : '';
}

function answerLooksLike(answer: FormSubmissionAnswerRow, tokens: string[]): boolean {
  const haystack = normalizePlanningName(`${answer.field_key} ${answer.field_label}`.replace(/_/g, ' '));
  return tokens.some((token) => haystack.includes(normalizePlanningName(token)));
}

function extractPersonListEntries(answer: Pick<FormSubmissionAnswerRow, 'value_json' | 'value_text'> | null | undefined): FormPersonListEntry[] {
  if (!answer) return [];

  const fromJson = normalizeFormPersonListEntries(answer.value_json);
  if (fromJson.length > 0) return fromJson;

  if (typeof answer.value_text !== 'string' || answer.value_text.trim().length === 0) {
    return [];
  }

  return splitNameList(answer.value_text).map((displayName) => ({
    displayName,
    userId: null,
    source: 'manual' as const,
  }));
}

function splitNameList(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/\s*(?:\||,|;|\n|\r|\/|\+|&|\bund\b)\s*/iu)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0),
    ),
  );
}

function submissionDisplayName(submission: FormSubmissionWithAnswers): string {
  const fullName = submission.answers.find((answer) => (
    answerLooksLike(answer, ['vor und nachname', 'vollstaendiger name', 'vollstandiger name'])
  ));
  const fullNameValue = answerText(fullName);
  if (fullNameValue) return fullNameValue;

  const firstName = submission.answers.find((answer) => answerLooksLike(answer, ['vorname', 'firstname', 'given name']));
  const lastName = submission.answers.find((answer) => answerLooksLike(answer, ['nachname', 'lastname', 'surname', 'familienname']));
  const joined = [answerText(firstName), answerText(lastName)].filter(Boolean).join(' ').trim();
  if (joined) return joined;

  const nameAnswer = submission.answers.find((answer) => (
    answerLooksLike(answer, ['name'])
    && !answerLooksLike(answer, ['zimmer', 'wunsch', 'partner', 'hinweis', 'email', 'telefon'])
  ));
  const nameValue = answerText(nameAnswer);
  if (nameValue) return nameValue;

  // Do not fall back to buildSubmissionPreview here — it would produce "Datum: ... · E-Mail: ..."
  // as a person name, which misleads the room planning display.
  return `Unbekannte Person (${submission.id.slice(0, 8)})`;
}

function submissionUserId(submission: FormSubmissionWithAnswers): string | null {
  return typeof submission.user_id === 'string' && submission.user_id.trim()
    ? submission.user_id.trim()
    : null;
}

function buildNameAliases(displayName: string): Set<string> {
  const aliases = new Set<string>();
  const normalized = normalizePlanningName(displayName);
  if (normalized) aliases.add(normalized);
  return aliases;
}

function addUniqueStandaloneAliases(participants: RoomPlanningParticipant[]) {
  // Count how often each name part appears across all participants.
  // Only add a part as standalone alias when it uniquely identifies one person —
  // common first names shared by multiple participants must not silently match.
  const partCount = new Map<string, number>();
  for (const participant of participants) {
    for (const part of participant.normalizedName.split(' ').filter((p) => p.length >= 3)) {
      partCount.set(part, (partCount.get(part) ?? 0) + 1);
    }
  }

  for (const participant of participants) {
    for (const part of participant.normalizedName.split(' ').filter((p) => p.length >= 3)) {
      if ((partCount.get(part) ?? 0) === 1) {
        participant.aliases.add(part);
      }
    }
  }
}

function parseRoomSizePreference(rawValue: string): RoomSizePreference | null {
  const value = normalizePlanningName(rawValue);
  if (!value) return null;

  const plusMatch = value.match(/(\d+)\s*(?:\+|plus|oder mehr|mehr)/u);
  if (plusMatch) {
    const min = Number(plusMatch[1]);
    return Number.isFinite(min) ? { min, max: null, label: rawValue.trim() } : null;
  }

  const rangeMatch = value.match(/(\d+)\s*(?:-|bis|–)\s*(\d+)/u);
  if (rangeMatch) {
    const first = Number(rangeMatch[1]);
    const second = Number(rangeMatch[2]);
    if (Number.isFinite(first) && Number.isFinite(second)) {
      return { min: Math.min(first, second), max: Math.max(first, second), label: rawValue.trim() };
    }
  }

  const numberMatch = value.match(/(\d+)/u);
  if (numberMatch) {
    const exact = Number(numberMatch[1]);
    return Number.isFinite(exact) ? { min: exact, max: exact, label: rawValue.trim() } : null;
  }

  if (value.includes('klein')) return { min: 4, max: 4, label: rawValue.trim() };
  if (value.includes('mittel')) return { min: 5, max: 6, label: rawValue.trim() };
  if (value.includes('gross') || value.includes('groß')) return { min: 7, max: 8, label: rawValue.trim() };
  return null;
}

function parseBooleanWish(rawValue: string): boolean {
  const value = normalizePlanningName(rawValue);
  return /\b(ja|yes|true|1|einzel|allein|wunsch)\b/u.test(value)
    && !/\b(nein|no|false|0|egal|kein)\b/u.test(value);
}

function extractRoommateWishEntries(
  submission: FormSubmissionWithAnswers,
  personListFieldId: string | null | undefined,
): FormPersonListEntry[] {
  const selectedAnswer = personListFieldId
    ? submission.answers.find((entry) => entry.field_id === personListFieldId)
    : undefined;
  const wishes = extractPersonListEntries(selectedAnswer);
  const seen = new Set(wishes.map((entry) => `${entry.userId ?? ''}:${normalizePlanningName(entry.displayName)}`));

  for (const answer of submission.answers) {
    if (selectedAnswer && answer.field_id === selectedAnswer.field_id) continue;
    if (!answerLooksLike(answer, ['zimmerwunsch', 'zimmerpartner', 'wunschperson', 'wunsch personen'])) continue;
    for (const displayName of splitNameList(answerText(answer))) {
      const key = `:${normalizePlanningName(displayName)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      wishes.push({ displayName, userId: null, source: 'manual' });
    }
  }

  return wishes;
}

function extractPreferredRoomSize(submission: FormSubmissionWithAnswers): RoomSizePreference | null {
  const answer = submission.answers.find((entry) => (
    answerLooksLike(entry, ['zimmergroesse', 'zimmergrosse', 'zimmergröße', 'zimmergröße', 'bevorzugte groesse', 'bevorzugte grosse'])
  ));
  return parseRoomSizePreference(answerText(answer));
}

function extractSingleRoomWish(submission: FormSubmissionWithAnswers): boolean {
  const answer = submission.answers.find((entry) => answerLooksLike(entry, ['einzelzimmer', 'allein schlafen']));
  return parseBooleanWish(answerText(answer));
}

function extractPlanningNotes(submission: FormSubmissionWithAnswers): string {
  return submission.answers
    .filter((entry) => answerLooksLike(entry, ['hinweis', 'bemerkung', 'besonder', 'notiz']))
    .map(answerText)
    .filter(Boolean)
    .join(' | ');
}

export function buildRoomPlanningParticipants(
  submissions: FormSubmissionWithAnswers[],
  personListFieldId: string | null | undefined,
): RoomPlanningParticipant[] {
  const participants = submissions.map((submission) => {
    const displayName = submissionDisplayName(submission);
    const requestedRoommateEntries = extractRoommateWishEntries(submission, personListFieldId);
    const requestedRoommates = requestedRoommateEntries.map((entry) => entry.displayName);
    const requestedRoommateTokens = new Set(
      requestedRoommates
        .map((name) => normalizePlanningName(name))
        .filter(Boolean),
    );

    return {
      submission,
      submissionId: submission.id,
      displayName,
      userId: submissionUserId(submission),
      normalizedName: normalizePlanningName(displayName),
      aliases: buildNameAliases(displayName),
      requestedRoommateEntries,
      requestedRoommates,
      requestedRoommateTokens,
      preferredRoomSize: extractPreferredRoomSize(submission),
      wantsSingleRoom: extractSingleRoomWish(submission),
      notes: extractPlanningNotes(submission),
    };
  }).filter((participant) => participant.normalizedName.length > 0);

  addUniqueStandaloneAliases(participants);
  return participants;
}

function pairKey(a: string, b: string): string {
  return [a, b].sort().join('::');
}

function participantKey(participant: RoomPlanningParticipant): string {
  return participant.submission.id;
}

export function roomPlanningParticipantKey(participant: RoomPlanningParticipant): string {
  return participantKey(participant);
}

export function buildRoomPlanningParticipantIdentityIndex(
  participants: RoomPlanningParticipant[],
): RoomPlanningParticipantIdentityIndex {
  const bySubmissionId = new Map(participants.map((participant) => [participant.submission.id, participant]));
  const byUserId = new Map(
    participants
      .filter((participant) => participant.userId)
      .map((participant) => [participant.userId as string, participant]),
  );
  const byName = new Map<string, RoomPlanningParticipant[]>();

  for (const participant of participants) {
    const entries = byName.get(participant.normalizedName) ?? [];
    entries.push(participant);
    byName.set(participant.normalizedName, entries);
  }

  const byUniqueName = new Map<string, RoomPlanningParticipant>();
  for (const [normalizedName, entries] of byName.entries()) {
    if (entries.length === 1) byUniqueName.set(normalizedName, entries[0]!);
  }

  return { bySubmissionId, byUserId, byUniqueName, byName };
}

export function resolveRoomPlanningAssignmentParticipant(
  assignment: FormRoomAssignmentRow,
  index: RoomPlanningParticipantIdentityIndex,
): RoomPlanningParticipant | null {
  if (assignment.submission_id) {
    const participant = index.bySubmissionId.get(assignment.submission_id);
    if (participant) return participant;
  }
  if (assignment.source !== 'external' && assignment.user_id) {
    const participant = index.byUserId.get(assignment.user_id);
    if (participant) return participant;
  }
  if (assignment.source !== 'external') {
    const normalizedName = normalizePlanningName(assignment.display_name);
    if (normalizedName) return index.byUniqueName.get(normalizedName) ?? null;
  }
  return null;
}

function buildAliasMaps(participants: RoomPlanningParticipant[]) {
  const exact = new Map<string, RoomPlanningParticipant[]>();
  const aliases = new Map<string, RoomPlanningParticipant[]>();
  const byUserId = new Map<string, RoomPlanningParticipant>();

  for (const participant of participants) {
    if (participant.userId) byUserId.set(participant.userId, participant);

    const exactEntries = exact.get(participant.normalizedName) ?? [];
    exactEntries.push(participant);
    exact.set(participant.normalizedName, exactEntries);

    for (const alias of participant.aliases) {
      const entries = aliases.get(alias) ?? [];
      entries.push(participant);
      aliases.set(alias, entries);
    }
  }

  return { exact, aliases, byUserId };
}

function resolveWish(
  token: string,
  rawName: string,
  participants: RoomPlanningParticipant[],
  userId: string | null,
  maps = buildAliasMaps(participants),
): WishResolution {
  if (userId) {
    const profileMatch = maps.byUserId.get(userId);
    if (profileMatch) return { status: 'matched', target: profileMatch, rawName, token, userId };
    return { status: 'unmatched', rawName, token, userId };
  }

  const exactMatches = maps.exact.get(token) ?? [];
  if (exactMatches.length === 1) return { status: 'matched', target: exactMatches[0], rawName, token, userId };
  if (exactMatches.length > 1) return { status: 'ambiguous', rawName, token, candidates: exactMatches, userId };

  const aliasMatches = maps.aliases.get(token) ?? [];
  if (aliasMatches.length === 1) return { status: 'matched', target: aliasMatches[0], rawName, token, userId };
  if (aliasMatches.length > 1) return { status: 'ambiguous', rawName, token, candidates: aliasMatches, userId };

  return { status: 'unmatched', rawName, token, userId };
}

function buildRelationshipGraph(participants: RoomPlanningParticipant[]): RelationshipGraph {
  const maps = buildAliasMaps(participants);
  const byParticipant = new Map<string, WishResolution[]>();
  const directedTargets = new Map<string, Set<string>>();

  for (const participant of participants) {
    const resolutions: WishResolution[] = [];
    for (const requestedRoommate of participant.requestedRoommateEntries) {
      const rawName = requestedRoommate.displayName;
      const token = normalizePlanningName(rawName);
      if (!token) continue;
      const resolution = resolveWish(token, rawName, participants, requestedRoommate.userId, maps);
      if (resolution.status === 'matched' && participantKey(resolution.target) === participantKey(participant)) continue;
      resolutions.push(resolution);
      if (resolution.status === 'matched') {
        const sourceId = participantKey(participant);
        const targets = directedTargets.get(sourceId) ?? new Set<string>();
        targets.add(participantKey(resolution.target));
        directedTargets.set(sourceId, targets);
      }
    }
    byParticipant.set(participantKey(participant), resolutions);
  }

  const mutualPairs = new Set<string>();
  for (const [source, targets] of directedTargets.entries()) {
    for (const target of targets) {
      if (directedTargets.get(target)?.has(source)) mutualPairs.add(pairKey(source, target));
    }
  }

  return {
    byParticipant,
    directedTargets,
    mutualPairs,
    weight: (a, b) => {
      const aToB = directedTargets.get(participantKey(a))?.has(participantKey(b)) ?? false;
      const bToA = directedTargets.get(participantKey(b))?.has(participantKey(a)) ?? false;
      if (aToB && bToA) return 3;
      if (aToB || bToA) return 1;
      return 0;
    },
  };
}

export function countParticipantsWithoutWishes(participants: RoomPlanningParticipant[]): number {
  return participants.filter((p) => p.requestedRoommates.length === 0).length;
}

function buildParticipantConflicts(participants: RoomPlanningParticipant[], graph = buildRelationshipGraph(participants)): RoomPlanningConflict[] {
  const conflicts: RoomPlanningConflict[] = [];
  const byName = new Map<string, RoomPlanningParticipant[]>();

  for (const participant of participants) {
    const existing = byName.get(participant.normalizedName) ?? [];
    existing.push(participant);
    byName.set(participant.normalizedName, existing);
    // Participants without wishes are counted separately via countParticipantsWithoutWishes
    // and shown as a summary info, not as individual conflicts.
  }

  for (const duplicates of byName.values()) {
    if (duplicates.length <= 1) continue;
    conflicts.push({
      type: 'duplicate_name',
      label: duplicates[0].displayName,
      detail: `Name ist ${duplicates.length}x vorhanden. Wunsch-Zuordnung ist dadurch unsicher.`,
      names: duplicates.map((entry) => entry.displayName),
    });
  }

  for (const participant of participants) {
    for (const resolution of graph.byParticipant.get(participantKey(participant)) ?? []) {
      if (resolution.status === 'unmatched') {
        conflicts.push({
          type: resolution.userId ? 'profile_without_submission' : 'unmatched_wish',
          label: participant.displayName,
          detail: resolution.userId
            ? `${resolution.rawName} ist als App-Profil zugeordnet, hat aber keine eigene Einreichung in diesem Formular.`
            : `Nicht eindeutig gefunden: ${resolution.rawName}.`,
          names: [participant.displayName, resolution.rawName],
        });
      } else if (resolution.status === 'ambiguous') {
        conflicts.push({
          type: 'ambiguous_wish',
          label: participant.displayName,
          detail: `Wunsch „${resolution.rawName}" passt zu mehreren Personen: ${resolution.candidates.map((entry) => entry.displayName).join(', ')}.`,
          names: [participant.displayName, ...resolution.candidates.map((entry) => entry.displayName)],
        });
      }
    }

    const targets = graph.directedTargets.get(participantKey(participant)) ?? new Set<string>();
    for (const target of targets) {
      if (graph.directedTargets.get(target)?.has(participantKey(participant))) continue;
      const otherTargets = Array.from(graph.directedTargets.get(target) ?? []).filter((entry) => entry !== participantKey(participant));
      if (otherTargets.length === 0) continue;
      const targetParticipant = participants.find((entry) => participantKey(entry) === target);
      conflicts.push({
        type: 'one_way_conflict',
        label: participant.displayName,
        detail: `${participant.displayName} wünscht ${targetParticipant?.displayName ?? target}, aber diese Person nennt andere Wunschpersonen.`,
        names: [participant.displayName, targetParticipant?.displayName ?? target],
      });
    }
  }

  return conflicts;
}

export function isSleepingRoom(room: Pick<FormRoomRow, 'name' | 'capacity'> & Partial<Pick<FormRoomRow, 'type' | 'is_sleeping_room'>>): boolean {
  if (room.capacity <= 0) return false;
  if (room.is_sleeping_room === true) return true;
  if (room.is_sleeping_room === false) return false;

  const label = normalizePlanningName(`${room.type ?? ''} ${room.name}`);
  if (/(tagesraum|gruppenraum|aufenthalt|seminar|probe|speise|essen|saal|lager)/u.test(label)) {
    return false;
  }
  return true;
}

function roomSort(a: FormRoomRow, b: FormRoomRow): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });
}

function roomMatchesPreference(room: FormRoomRow, preference: RoomSizePreference | null): number {
  if (!preference) return 0;
  if (room.capacity < preference.min) return preference.min - room.capacity;
  if (preference.max !== null && room.capacity > preference.max) return room.capacity - preference.max;
  return -2;
}

function selectBestRoom(
  rooms: FormRoomRow[],
  remainingByRoomId: Map<string, number>,
  group: RoomPlanningParticipant[],
): FormRoomRow | null {
  return rooms
    .filter((room) => isSleepingRoom(room) && (remainingByRoomId.get(room.id) ?? 0) > 0)
    .sort((a, b) => {
      const aRemaining = remainingByRoomId.get(a.id) ?? 0;
      const bRemaining = remainingByRoomId.get(b.id) ?? 0;
      const aFitsAll = aRemaining >= group.length ? 0 : 1;
      const bFitsAll = bRemaining >= group.length ? 0 : 1;
      if (aFitsAll !== bFitsAll) return aFitsAll - bFitsAll;

      const aPreferencePenalty = group.reduce((sum, participant) => sum + roomMatchesPreference(a, participant.preferredRoomSize), 0);
      const bPreferencePenalty = group.reduce((sum, participant) => sum + roomMatchesPreference(b, participant.preferredRoomSize), 0);
      if (aPreferencePenalty !== bPreferencePenalty) return aPreferencePenalty - bPreferencePenalty;

      const aWaste = Math.max(0, aRemaining - Math.min(group.length, aRemaining));
      const bWaste = Math.max(0, bRemaining - Math.min(group.length, bRemaining));
      if (aWaste !== bWaste) return aWaste - bWaste;

      if (aRemaining !== bRemaining) return aRemaining - bRemaining;
      return roomSort(a, b);
    })[0] ?? null;
}

function selectRelationshipChunk(
  pending: RoomPlanningParticipant[],
  size: number,
  graph: RelationshipGraph,
): RoomPlanningParticipant[] {
  if (size >= pending.length) return [...pending];
  if (size <= 0) return [];

  const selected: RoomPlanningParticipant[] = [];
  const candidates = [...pending].sort((a, b) => {
    const aScore = pending.reduce((sum, other) => sum + graph.weight(a, other), 0);
    const bScore = pending.reduce((sum, other) => sum + graph.weight(b, other), 0);
    if (bScore !== aScore) return bScore - aScore;
    return a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' });
  });

  selected.push(candidates.shift() as RoomPlanningParticipant);
  while (selected.length < size && candidates.length > 0) {
    candidates.sort((a, b) => {
      const aScore = selected.reduce((sum, other) => sum + graph.weight(a, other), 0);
      const bScore = selected.reduce((sum, other) => sum + graph.weight(b, other), 0);
      if (bScore !== aScore) return bScore - aScore;
      return a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' });
    });
    selected.push(candidates.shift() as RoomPlanningParticipant);
  }

  return selected;
}

function buildWishGroups(participants: RoomPlanningParticipant[], graph = buildRelationshipGraph(participants)): RoomPlanningParticipant[][] {
  const byId = new Map(participants.map((participant) => [participantKey(participant), participant]));
  const neighbours = new Map<string, Set<string>>();

  // Only mutual (bidirectional) wishes form a group — one-way wishes are context only.
  // This prevents someone who wishes A (but isn't wished back) from being merged into A's core group.
  for (const [source, targets] of graph.directedTargets.entries()) {
    for (const target of targets) {
      if (!graph.directedTargets.get(target)?.has(source)) continue;
      const sourceNeighbours = neighbours.get(source) ?? new Set<string>();
      sourceNeighbours.add(target);
      neighbours.set(source, sourceNeighbours);
    }
  }

  const visited = new Set<string>();
  const groups: RoomPlanningParticipant[][] = [];

  for (const participant of participants) {
    if (visited.has(participantKey(participant))) continue;
    const queue = [participantKey(participant)];
    const tokens: string[] = [];

    while (queue.length > 0) {
      const token = queue.shift() as string;
      if (visited.has(token)) continue;
      visited.add(token);
      tokens.push(token);
      for (const next of neighbours.get(token) ?? []) {
        if (!visited.has(next)) queue.push(next);
      }
    }

    const group = tokens
      .map((token) => byId.get(token))
      .filter((entry): entry is RoomPlanningParticipant => Boolean(entry))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de', { sensitivity: 'base' }));
    if (group.length > 0) groups.push(group);
  }

  return groups.sort((a, b) => {
    if (b.length !== a.length) return b.length - a.length;
    return a[0]?.displayName.localeCompare(b[0]?.displayName ?? '', 'de', { sensitivity: 'base' }) ?? 0;
  });
}

export function buildRoomWishGroupSummaries(participants: RoomPlanningParticipant[]): RoomWishGroupSummary[] {
  const graph = buildRelationshipGraph(participants);
  return buildWishGroups(participants, graph)
    .map((group) => {
      const groupIds = new Set(group.map(participantKey));
      const mutualWishes: string[] = [];
      const oneWayWishes: string[] = [];
      const unresolvedWishes: string[] = [];

      for (const participant of group) {
        const participantId = participantKey(participant);
        for (const resolution of graph.byParticipant.get(participantId) ?? []) {
          if (resolution.status !== 'matched') {
            unresolvedWishes.push(`${participant.displayName} → ${resolution.rawName}`);
            continue;
          }
          const targetId = participantKey(resolution.target);
          if (!groupIds.has(targetId)) continue;
          const label = `${participant.displayName} → ${resolution.target.displayName}`;
          if (graph.mutualPairs.has(pairKey(participantId, targetId))) {
            const sortedLabel = [participant.displayName, resolution.target.displayName]
              .sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))
              .join(' ↔ ');
            if (!mutualWishes.includes(sortedLabel)) mutualWishes.push(sortedLabel);
          } else {
            oneWayWishes.push(label);
          }
        }
      }

      for (const participant of participants) {
        const participantId = participantKey(participant);
        if (groupIds.has(participantId)) continue;
        for (const resolution of graph.byParticipant.get(participantId) ?? []) {
          if (resolution.status !== 'matched') continue;
          const targetId = participantKey(resolution.target);
          if (!groupIds.has(targetId)) continue;
          if (graph.mutualPairs.has(pairKey(participantId, targetId))) continue;
          const label = `${participant.displayName} → ${resolution.target.displayName}`;
          if (!oneWayWishes.includes(label)) oneWayWishes.push(label);
        }
      }

      return {
        participantIds: group.map(participantKey),
        names: group.map((entry) => entry.displayName),
        matchCount: mutualWishes.length * 2 + oneWayWishes.length,
        isSingleGroup: group.length === 1 && mutualWishes.length === 0 && oneWayWishes.length === 0,
        mutualWishes,
        oneWayWishes,
        unresolvedWishes,
      };
    })
    .sort((a, b) => {
      if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
      if (b.names.length !== a.names.length) return b.names.length - a.names.length;
      return (a.names[0] ?? '').localeCompare(b.names[0] ?? '', 'de', { sensitivity: 'base' });
    });
}

function proposalNoteFor(room: FormRoomRow, chunk: RoomPlanningParticipant[], groupWasSplit: boolean, graph: RelationshipGraph): string {
  const notes: string[] = [];
  if (chunk.length > 1) notes.push('Wunschgruppe zusammen geplant');
  if (groupWasSplit) notes.push('Wunschgruppe wegen Kapazität geteilt');
  const mutualCount = chunk.reduce((count, participant, index) => (
    count + chunk.slice(index + 1).filter((other) => graph.mutualPairs.has(pairKey(participantKey(participant), participantKey(other)))).length
  ), 0);
  if (mutualCount > 0) notes.push(`${mutualCount} gegenseitige Wünsche berücksichtigt`);
  const preferredMatches = chunk.filter((participant) => roomMatchesPreference(room, participant.preferredRoomSize) < 0).length;
  if (preferredMatches > 0) notes.push('Zimmergröße passt zu Präferenz');
  const singleWishes = chunk.filter((participant) => participant.wantsSingleRoom).length;
  if (singleWishes > 0 && chunk.length > 1) notes.push('Einzelzimmerwunsch nicht automatisch garantiert');
  return notes.join(' · ');
}

export function buildRoomProposal(
  participants: RoomPlanningParticipant[],
  rooms: FormRoomRow[],
  lockedAssignments: FormRoomAssignmentRow[] = [],
): { assignments: ProposedRoomAssignment[]; conflicts: RoomPlanningConflict[] } {
  const graph = buildRelationshipGraph(participants);
  const identityIndex = buildRoomPlanningParticipantIdentityIndex(participants);
  const sortedRooms = [...rooms].filter((room) => room.capacity > 0).sort(roomSort);
  const sleepingRooms = sortedRooms.filter(isSleepingRoom);
  const remainingByRoomId = new Map(sortedRooms.map((room) => [room.id, room.capacity]));

  for (const locked of lockedAssignments) {
    if (!locked.room_id) continue;
    remainingByRoomId.set(locked.room_id, Math.max(0, (remainingByRoomId.get(locked.room_id) ?? 0) - 1));
  }

  const lockedParticipantIds = new Set(
    lockedAssignments
      .map((assignment) => resolveRoomPlanningAssignmentParticipant(assignment, identityIndex))
      .filter((participant): participant is RoomPlanningParticipant => Boolean(participant))
      .map(participantKey),
  );
  const proposal: ProposedRoomAssignment[] = [];
  const conflicts: RoomPlanningConflict[] = buildParticipantConflicts(participants, graph);

  if (sleepingRooms.length === 0 && participants.length > lockedParticipantIds.size) {
    conflicts.push({
      type: 'missing_capacity',
      label: 'Keine Schlafzimmer',
      detail: 'Es gibt kein nutzbares Schlafzimmer mit Kapazität.',
      names: participants.map((entry) => entry.displayName),
    });
  }

  for (const group of buildWishGroups(participants, graph)) {
    const unlockedGroup = group.filter((participant) => !lockedParticipantIds.has(participantKey(participant)));
    if (unlockedGroup.length === 0) continue;

    let pending = [...unlockedGroup];
    const maxCapacity = sleepingRooms.reduce((max, room) => Math.max(max, remainingByRoomId.get(room.id) ?? 0), 0);
    if (maxCapacity > 0 && pending.length > maxCapacity) {
      conflicts.push({
        type: 'group_too_large',
        label: pending.map((entry) => entry.displayName).join(', '),
        detail: `Wunschgruppe mit ${pending.length} Personen ist größer als das größte freie Schlafzimmer (${maxCapacity}).`,
        names: pending.map((entry) => entry.displayName),
      });
    }

    while (pending.length > 0) {
      const room = selectBestRoom(sleepingRooms, remainingByRoomId, pending);
      if (!room) {
        for (const participant of pending) {
          proposal.push({
            participant,
            roomId: null,
            notes: 'Nicht automatisch zugeordnet: keine freien Betten in Schlafzimmern',
          });
        }
        conflicts.push({
          type: 'missing_capacity',
          label: pending.map((entry) => entry.displayName).join(', '),
          detail: 'Keine freien Betten in nutzbaren Schlafzimmern für diese Personen.',
          names: pending.map((entry) => entry.displayName),
        });
        break;
      }

      const available = remainingByRoomId.get(room.id) ?? 0;
      const chunk = selectRelationshipChunk(pending, available, graph);
      const chunkIds = new Set(chunk.map((entry) => participantKey(entry)));
      pending = pending.filter((entry) => !chunkIds.has(participantKey(entry)));
      remainingByRoomId.set(room.id, Math.max(0, available - chunk.length));

      const groupWasSplit = pending.length > 0;
      if (groupWasSplit) {
        conflicts.push({
          type: 'partial_group',
          label: group.map((entry) => entry.displayName).join(', '),
          detail: `Wunschgruppe wurde wegen Zimmerkapazität aufgeteilt; Teilgruppe in ${room.name}.`,
          names: group.map((entry) => entry.displayName),
        });
      }

      for (const participant of chunk) {
        proposal.push({
          participant,
          roomId: room.id,
          notes: proposalNoteFor(room, chunk, groupWasSplit, graph),
        });
      }
    }
  }

  return { assignments: proposal, conflicts };
}

export function buildRoomPlanningConflicts(
  participants: RoomPlanningParticipant[],
  rooms: FormRoomRow[],
  assignments: FormRoomAssignmentRow[],
): RoomPlanningConflict[] {
  const graph = buildRelationshipGraph(participants);
  const conflicts = buildParticipantConflicts(participants, graph);
  const roomById = new Map(rooms.map((room) => [room.id, room]));
  const identityIndex = buildRoomPlanningParticipantIdentityIndex(participants);
  const assignmentByParticipantId = new Map<string, FormRoomAssignmentRow[]>();
  const assignmentByRoomId = new Map<string, FormRoomAssignmentRow[]>();

  for (const assignment of assignments) {
    const participant = resolveRoomPlanningAssignmentParticipant(assignment, identityIndex);

    if (assignment.submission_id && !identityIndex.bySubmissionId.has(assignment.submission_id) && !participant) {
      conflicts.push({
        type: 'stale_assignment',
        label: assignment.display_name,
        detail: 'Person ist nicht mehr in den aktuellen Einreichungen vorhanden.',
        names: [assignment.display_name],
      });
    }

    if (participant) {
      const entries = assignmentByParticipantId.get(participant.submission.id) ?? [];
      entries.push(assignment);
      assignmentByParticipantId.set(participant.submission.id, entries);
    }

    const roomKey = assignment.room_id ?? '';
    const roomEntries = assignmentByRoomId.get(roomKey) ?? [];
    roomEntries.push(assignment);
    assignmentByRoomId.set(roomKey, roomEntries);

    if (!assignment.room_id) continue;
    const room = roomById.get(assignment.room_id);
    if (!room) {
      conflicts.push({
        type: 'invalid_room',
        label: assignment.display_name,
        detail: 'Zuordnung verweist auf ein nicht mehr vorhandenes Zimmer.',
        names: [assignment.display_name],
      });
    } else if (!isSleepingRoom(room)) {
      conflicts.push({
        type: 'non_sleeping_room_used',
        label: room.name,
        detail: `${assignment.display_name} liegt in einem Raum, der nicht als Schlafzimmer markiert ist.`,
        names: [assignment.display_name],
      });
    }
  }

  for (const [submissionId, entries] of assignmentByParticipantId.entries()) {
    if (entries.length <= 1) continue;
    conflicts.push({
      type: 'duplicate_assignment',
      label: entries[0].display_name,
      detail: `Einreichung ${submissionId.slice(0, 8)} ist mehrfach eingeteilt.`,
      names: entries.map((entry) => entry.display_name),
    });
  }

  for (const participant of participants) {
    const entries = assignmentByParticipantId.get(participant.submission.id) ?? [];
    if (entries.length === 0) {
      conflicts.push({
        type: 'missing_person',
        label: participant.displayName,
        detail: 'Person aus den Einreichungen fehlt in der Belegung.',
        names: [participant.displayName],
      });
    }
  }

  for (const room of rooms) {
    if (room.capacity <= 0) {
      conflicts.push({
        type: 'invalid_room',
        label: room.name,
        detail: 'Zimmer hat keine gültige Bettenzahl.',
        names: [room.name],
      });
    }

    const entries = assignmentByRoomId.get(room.id) ?? [];
    if (entries.length > room.capacity) {
      conflicts.push({
        type: 'over_capacity',
        label: room.name,
        detail: `Überbelegt: ${entries.length}/${room.capacity}.`,
        names: entries.map((entry) => entry.display_name),
      });
    }
  }

  for (const participant of participants) {
    const assignment = assignmentByParticipantId.get(participant.submission.id)?.[0];
    if (!assignment) continue;

    if (!assignment.room_id) {
      conflicts.push({
        type: 'missing_capacity',
        label: participant.displayName,
        detail: 'Person ist noch keinem Zimmer zugeordnet.',
        names: [participant.displayName],
      });
      continue;
    }

    const roomEntries = assignmentByRoomId.get(assignment.room_id) ?? [];
    if (participant.wantsSingleRoom && roomEntries.length !== 1) {
      conflicts.push({
        type: 'single_room_violation',
        label: participant.displayName,
        detail: `Einzelzimmerwunsch ist nicht erfüllt: ${roomEntries.length} Personen im Zimmer.`,
        names: [participant.displayName, ...roomEntries.map((entry) => entry.display_name)],
      });
    }

    for (const resolution of graph.byParticipant.get(participantKey(participant)) ?? []) {
      if (resolution.status !== 'matched') continue;
      const wishedAssignment = assignmentByParticipantId.get(resolution.target.submission.id)?.[0];
      if (!wishedAssignment || !wishedAssignment.room_id || wishedAssignment.room_id !== assignment.room_id) {
        conflicts.push({
          type: 'wish_unfulfilled',
          label: participant.displayName,
          detail: `Zimmerwunsch mit ${resolution.target.displayName} ist nicht erfüllt.`,
          names: [participant.displayName, resolution.target.displayName],
        });
      }
    }
  }

  return conflicts;
}

export function roomPlanningConflictLabel(type: RoomPlanningConflict['type']): string {
  switch (type) {
    case 'ambiguous_wish':
      return 'Name unklar';
    case 'duplicate_assignment':
      return 'Doppelt';
    case 'duplicate_name':
      return 'Name doppelt';
    case 'group_too_large':
      return 'Gruppe zu groß';
    case 'invalid_room':
      return 'Zimmer prüfen';
    case 'missing_capacity':
      return 'Nicht zugeordnet';
    case 'missing_person':
      return 'Person fehlt';

    case 'non_sleeping_room_used':
      return 'Kein Schlafraum';
    case 'one_way_conflict':
      return 'Einseitig';
    case 'over_capacity':
      return 'Überbelegt';
    case 'partial_group':
      return 'Geteilt';
    case 'profile_without_submission':
      return 'Profil ohne Antwort';
    case 'single_room_violation':
      return 'Einzelwunsch';
    case 'stale_assignment':
      return 'Nicht mehr gültig';
    case 'unmatched_wish':
      return 'Name offen';
    case 'wish_unfulfilled':
      return 'Wunsch offen';
    default:
      return type;
  }
}

export function roomPlanningConflictColor(type: RoomPlanningConflict['type']): 'gray' | 'red' | 'yellow' {
  switch (type) {
    case 'duplicate_assignment':
    case 'group_too_large':
    case 'invalid_room':
    case 'missing_capacity':
    case 'missing_person':
    case 'non_sleeping_room_used':
    case 'over_capacity':
    case 'stale_assignment':
      return 'red';
    default:
      return 'yellow';
  }
}
